/**
 * Column metadata helpers for Claims Summary tables.
 *
 * Delegates to the global `getProps()` system for any field that exists in a
 * resource's metadata (e.g. "claimant_name" from CLAIMANTS, "member_id" from
 * CLAIMANTS). Only defines local overrides for truly computed columns that
 * don't map to any single resource field.
 *
 * Used by: SummaryTable, WatchListTable, and the Excel export.
 */

import { getProps, type ColumnProps } from "@/utils/helpers/columnsHelper";
import { featureFlags } from "@/utils/featureFlags";
import { ResourceName } from "@/types";

// ==================== TYPES ====================

interface ComputedColumnMeta {
  label: string;
  /** Tooltip / HintIcon text — omit when no hint is needed */
  hint?: string;
}

// ==================== RESOURCE-BACKED COLUMNS ====================
// These pull labels + tooltips from the global allColumnsMetadata.json
// via getProps(). The key here maps a short alias used in our tables
// to the actual resource field + record type.

interface ResourceColumnDef {
  field: string;
  resource: ResourceName;
}

const RESOURCE_COLUMNS: Record<string, ResourceColumnDef> = {
  claimant: { field: "claimant_name", resource: ResourceName.CLAIMANTS },
  memberId: { field: "member_id", resource: ResourceName.CLAIMANTS },
};

// ==================== COMPUTED COLUMNS ====================
// These are derived/calculated values that don't exist in any single
// resource's metadata. Labels and hints are defined locally.

const COMPUTED_COLUMNS: Record<string, ComputedColumnMeta> = {
  // SummaryTable columns
  // #7338: with the deductible basis on, a calculated-authority figure is what
  // counts toward the term's specific deductible under the contract window, not
  // every payment; a reported-authority row is the carrier's YTD either way. Same
  // figure as the claimant page's Total Paid card, where the dropped lines are
  // listed. Flag off keeps the legacy wording (stage, sandbox, demo, e2e).
  totalYTD: {
    label: "Total YTD",
    hint: featureFlags.deductibleBasisViewEnabled
      ? "Medical + RX policy-year spend as shown on the claimant page's Total Paid: calculated spend counts only lines inside the contract basis window; a carrier-reported YTD is used as provided."
      : "Full policy-year health plan spend (Medical + RX)",
  },
  deductible: { label: "Deductible", hint: "Lasered deductible applied when applicable" },
  owed: { label: "Owed", hint: "Total filed with the carrier (excess over deductible)" },
  variance: { label: "Variance", hint: "Owed − Reimbursed − Denied. Positive = outstanding balance." },

  // Aggregate Specific Deductible drawdown (#3302) — conditional, ASD policies only
  specificExcess: {
    label: "Specific Excess",
    hint: "Paid spend over this claimant's effective specific deductible, before the aggregate specific deductible is applied.",
  },
  asdConsumed: {
    label: "Applied to Agg Spec",
    hint: "Portion of this claimant's specific excess absorbed by the policy's aggregate specific deductible. While the corridor is still accumulating, the carrier owes nothing.",
  },
  carrierPayable: {
    label: "Carrier Payable",
    hint: "Specific excess minus the amount applied to the aggregate specific deductible — what the carrier owes once the corridor is breached.",
  },

  // WatchListTable columns
  watchListTotalYTD: { label: "Total YTD", hint: "Medical + RX spend from claim items in the selected period" },
  percentOfDeductible: { label: "% of Deductible" },
  remaining: { label: "Remaining" },
  threshold: { label: "Status" },

  // RowDetailPanel (SummaryTable expand) — filed breakdown
  medical: { label: "Medical" },
  rx: { label: "RX" },
  requested: { label: "Requested" },
  reimbursed: { label: "Reimbursed" },
  pended: { label: "Pended" },
  denied: { label: "Denied" },
};

// ==================== PUBLIC API ====================

/**
 * Retrieve the label for a claims summary column.
 *
 * Checks resource-backed metadata first (via global `getProps`),
 * then falls back to local computed column definitions.
 */
export const getColumnLabel = (key: string): string => {
  const resourceDef = RESOURCE_COLUMNS[key];
  if (resourceDef) {
    const props = getProps(resourceDef.field, resourceDef.resource);
    // If getProps returns a valid label (not the "Label missing..." fallback), use it
    if (!props.label.startsWith("Label missing")) {
      return props.label;
    }
  }

  return COMPUTED_COLUMNS[key]?.label ?? key;
};

/**
 * Retrieve the tooltip hint for a claims summary column, if one exists.
 *
 * For resource-backed columns, returns the tooltip from global metadata.
 * For computed columns, returns the locally defined hint.
 */
export const getColumnHint = (key: string): string | undefined => {
  const resourceDef = RESOURCE_COLUMNS[key];
  if (resourceDef) {
    const props = getProps(resourceDef.field, resourceDef.resource);
    if (!props.title.startsWith("Tooltip missing")) {
      return props.title;
    }
  }

  return COMPUTED_COLUMNS[key]?.hint;
};

/**
 * Retrieve full ColumnProps for a resource-backed column.
 *
 * Useful when you need source/name/title in addition to the label,
 * e.g. for building export headers with tooltips.
 */
export const getResourceColumnProps = (key: string): ColumnProps | undefined => {
  const resourceDef = RESOURCE_COLUMNS[key];
  if (!resourceDef) return undefined;
  return getProps(resourceDef.field, resourceDef.resource);
};
